import React, { useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import urlJoin from 'url-join';
import throttle from 'lodash.throttle';
import { MultipurposePaywall } from '@yola/webapp-plugins';
import { selectors, verifiers, constants } from '@yola/subscription-manager-js';
import redirectToUrl from 'src/js/utils/redirect-to-url';
import useFeatureFlags from '../../feature-flags/hooks/use-feature-flags';
import segment from '../../analytics/segment';
import user from '../../user';
import dialogs from '../../dialogs';
import config from '../../config';
import {
  premiumFeatures,
  productCategories,
  purchaseFlowTypes,
} from '../../common/constants/premium-features';
import { PERIOD_VALUES } from '../../common/constants/terms';
import { DEFAULT_INITIATOR } from '../../common/constants';
import useDomainsExtraPrice from '../../domain-selector/hooks/use-domains-extra-price';
import usePaymentMethods from '../hooks/use-payment-methods';
import getHighlightedHostingPackageFeatures from '../helpers/get-highlighted-hosting-package-features';
import extraPrices from '../../domain-selector/constants/extra-price-types';
import useAdaptiveViewport from '../../common/hooks/use-adaptive-viewport';
import useHideZendeskOnTouchDevice from '../../help-center/zendesk/hooks/use-hide-zendesk-on-touch-device';
import getActivePromotions from '../helpers/get-active-promotions';
import isDifmOfferBannerVisible from '../helpers/is-difm-offer-banner-visible';
import isLimitedOfferAvailable from '../helpers/is-limited-offer-available';
import getCaptionsForPaywall from '../helpers/get-captions-for-paywall';
import utils from '../../webapp-plugins/utils';
import freeTrialEligiblePremiumFeatures from '../constants/free-trial-eligible-premium-features';

const {
  constants: { events },
  track,
  trackAsync,
} = segment;
const {
  SubscriptionType: { B2C_HOSTING_FREE },
} = constants;

const defaultExtraProducts = [];
const defaultActionId = 'redirect_open';

function PaywallContainer({
  initiator = '',
  premiumFeature = premiumFeatures.DEFAULT,
  domain = '',
  periodToggle = true,
  extraProducts = defaultExtraProducts,
  term = '',
  triggerId = '',
  actionId = defaultActionId,
  paymentBackUrl = '',
}) {
  const currentPlanType = useSelector(selectors.getCurrentPackageType);
  const activeHostingSubscription = useSelector(selectors.getActiveHostingSubscription);
  const { currency } = useSelector(user.selectors.getUserPreferences, shallowEqual);
  const { id: userId } = useSelector(user.selectors.getUserData, shallowEqual);
  const baseUrl = useSelector(config.selectors.getMyyolaUrl);
  const subscriptionsList = useSelector(selectors.getSubscriptionList, shallowEqual);
  const availablePlans = useSelector(selectors.getHostingPackages, shallowEqual);
  const paymentMethods = usePaymentMethods();
  const partnerData = useSelector(user.selectors.getPartnerData, shallowEqual);
  const moneyBackGuarantee = useSelector(user.selectors.getMoneyBackGuarantee);
  const isB2C = useSelector(user.selectors.getIsB2C);

  const subscriptionsTypes = subscriptionsList.map(({ type }) => type);
  const hasEverHadPaidHostingSubscription = verifiers.hasPaidHostingPackageType(subscriptionsTypes);

  const onBackButtonClick = useCallback(() => {
    window.history.back();
  }, []);

  const hasExtraProducts = Boolean(extraProducts.length);
  const isEcommercePlanSelected =
    hasExtraProducts && extraProducts.some(verifiers.isEcommercePlanType);

  const [featureFlags] = useFeatureFlags([
    'ai_site_wizard',
    'ai_page_wizard',
    'ai_block_generator',
    'ai_text_assistant',
    'ai_text_assistant_rewrite',
    'ai_text_assistant_rewrite_v2',
    'ai_text_assistant_write',
    'site_export',
    'freedomain',
    'composite_group',
    'ai_localization',
    'free_trial_upsell',
    'nn_difm_promo',
    'remove_partner_ads',
    'gold_reinforcement',
    'intro_price_discounts',
  ]);

  const {
    free_trial_upsell: isFreeTrialUpsellFeatureFlagEnabled,
    nn_difm_promo: isNnDifmPromoFeatureFlagEnabled,
    intro_price_discounts: isIntroPriceDiscountsFeatureFlagEnabled,
  } = featureFlags;

  const canOfferFreeTrial =
    isFreeTrialUpsellFeatureFlagEnabled && !hasEverHadPaidHostingSubscription;
  const isPremiumFeatureSupportsFreeTrial =
    freeTrialEligiblePremiumFeatures.includes(premiumFeature);
  const isFreeTrialAvailable = canOfferFreeTrial && isPremiumFeatureSupportsFreeTrial;
  const isActiveTrialSubscription =
    activeHostingSubscription && verifiers.isTrialSubscription(activeHostingSubscription);
  const domainDiscount = useDomainsExtraPrice(isFreeTrialAvailable);

  const isTrialDomainDiscountAvailable =
    domainDiscount === extraPrices.ANNUAL_TRIAL_PACKAGE ||
    (isActiveTrialSubscription && domainDiscount === extraPrices.NO_DISCOUNTS);

  const dispatch = useDispatch();

  const trackingData = useMemo(
    () => ({
      siteId: null,
      templateBuildSlug: null,
      premiumFeature,
      purchaseFlowType: isB2C ? purchaseFlowTypes.B2C : purchaseFlowTypes.WL,
      productCategory: productCategories.LATITUDE,
    }),
    [premiumFeature, isB2C]
  );

  const paywallProps = useMemo(
    () => ({
      initiator: initiator || DEFAULT_INITIATOR,
      ...(isB2C && domain && { domainName: domain }),
      ...(term && { defaultPaymentPeriod: PERIOD_VALUES[term] }),
      ...(paymentBackUrl && {
        closeUrl: paymentBackUrl,
        successUrl: paymentBackUrl,
        backUrl: paymentBackUrl,
        failUrl: paymentBackUrl,
      }),
      ...(premiumFeature === premiumFeatures.TRANSFER_DOMAIN && {
        closeUrl: urlJoin(baseUrl, '/domains/#transfer-domain'),
        successUrl: urlJoin(baseUrl, '/domains/#transfer-domain'),
        backUrl: urlJoin(baseUrl, '/domains/#transfer-domain'),
        failUrl: urlJoin(baseUrl, '/domains/#transfer-domain'),
      }),
      ...(hasExtraProducts && { products: extraProducts }),
      ...(periodToggle === false && { periodTogglerHidden: true }),
    }),
    [
      initiator,
      domain,
      term,
      premiumFeature,
      baseUrl,
      hasExtraProducts,
      extraProducts,
      isB2C,
      periodToggle,
      paymentBackUrl,
    ]
  );

  const handleBackButtonClick = async () => {
    await trackAsync(events.PLAN_SELECTOR_BACK_BUTTON_CLICKED, trackingData);

    dispatch(dialogs.actions.hide());
    onBackButtonClick();
  };

  const handlePeriodChange = (selectedPlan, selectedTerm) => {
    track(events.PLAN_SELECTOR_TERM_TOGGLE_CLICKED, {
      ...trackingData,
      selectedTerm,
    });
  };

  const handleTooltipHover = (featureTooltip) => {
    track(events.PLAN_SELECTOR_TOOLTIP_DISPLAYED, {
      ...trackingData,
      featureTooltip,
    });
  };

  useEffect(() => {
    track(events.PLAN_SELECTOR_DISPLAYED, {
      ...trackingData,
      actionId,
      activePromotions: getActivePromotions({
        premiumFeature,
        isNnDifmPromoFeatureFlagEnabled,
        isFreeTrialAvailable,
        isFreeTrialUpsellFeatureFlagEnabled,
      }),
      trial: isFreeTrialAvailable,
    });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const throttledPageScroll = throttle(() => {
      const documentHeight = document.body.offsetHeight;
      const currentScroll = window.scrollY + window.innerHeight;

      if (currentScroll >= documentHeight) {
        track(events.PLAN_SELECTOR_SCROLLED_TO_BOTTOM, trackingData);
      }
    }, 100);

    document.addEventListener('scroll', throttledPageScroll);

    return () => document.removeEventListener('scroll', throttledPageScroll);
  }, [trackingData]);

  const handleSelect = async (paymentUrl, selectedPlan, selectedTerm, paymentParams = {}) => {
    const billingUrl = isFreeTrialAvailable
      ? utils.generatePaymentUrl({ ...paymentParams, trial_package: '1' })
      : paymentUrl;

    dispatch(dialogs.actions.show(dialogs.dialogTypes.PENDING));

    Promise.all([
      trackAsync(events.PLAN_SELECTOR_PLAN_SELECT_BUTTON_CLICKED, {
        ...trackingData,
        selectedTerm,
        selectedPlan,
      }),
      trackAsync(events.UPGRADE_INITIATED, {
        ...trackingData,
        trial: isFreeTrialAvailable,
      }),
    ]).then(() => redirectToUrl(billingUrl));
  };

  useAdaptiveViewport();
  useHideZendeskOnTouchDevice();

  // we have to show the paywall only if feature flags is loaded
  if (!Object.keys(featureFlags).length) {
    return null;
  }

  const isLimitedDifmOfferAvailable = isNnDifmPromoFeatureFlagEnabled && isLimitedOfferAvailable();
  const isIntroDiscountAvailable =
    isIntroPriceDiscountsFeatureFlagEnabled && !hasEverHadPaidHostingSubscription;

  const isDifmBannerShown = isLimitedDifmOfferAvailable && isDifmOfferBannerVisible(premiumFeature);
  const disabledPlans = isFreeTrialAvailable || isIntroDiscountAvailable ? [B2C_HOSTING_FREE] : [];

  const captions = getCaptionsForPaywall({
    isB2C,
    triggerId,
    premiumFeature,
    isFreeTrialAvailable,
  });

  return (
    <MultipurposePaywall
      captions={captions}
      currentPlan={currentPlanType}
      currentTerm={activeHostingSubscription?.term}
      userId={userId}
      currency={currency}
      onSelect={handleSelect}
      onBackButtonClick={handleBackButtonClick}
      onTooltipHover={handleTooltipHover}
      onPeriodChange={handlePeriodChange}
      isB2C={isB2C}
      availablePlans={availablePlans}
      moneyBackGuarantee={moneyBackGuarantee}
      paymentMethods={paymentMethods}
      partnerId={partnerData.id}
      partnerName={partnerData.name}
      featureFlags={featureFlags}
      domainName={domain}
      domainDiscount={domainDiscount}
      highlightedFeatures={getHighlightedHostingPackageFeatures(
        premiumFeature,
        !!domainDiscount,
        isTrialDomainDiscountAvailable
      )}
      periodTogglerHidden={isEcommercePlanSelected}
      isLimitedOfferAvailable={isLimitedDifmOfferAvailable}
      isDifmOfferBannerVisible={isDifmBannerShown}
      isTrialDomainDiscountAvailable={isTrialDomainDiscountAvailable}
      disabledPlans={disabledPlans}
      isFreeTrialAvailable={isFreeTrialAvailable}
      isIntroDiscountAvailable={isIntroDiscountAvailable}
      {...paywallProps}
    />
  );
}

PaywallContainer.propTypes = {
  initiator: PropTypes.string,
  premiumFeature: PropTypes.string,
  domain: PropTypes.string,
  extraProducts: PropTypes.arrayOf(PropTypes.string),
  term: PropTypes.string,
  triggerId: PropTypes.string,
  actionId: PropTypes.string,
  periodToggle: PropTypes.bool,
  domainProposal: PropTypes.bool,
  paymentBackUrl: PropTypes.string,
};

export default PaywallContainer;
